import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { categoryLabel, fmtEUR } from "@/lib/constants";

// Fix default icon paths (webpack strips the images otherwise)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const goldPin = new L.DivIcon({
    className: "renforteo-map-pin",
    html: `<div style="width:32px;height:32px;background:linear-gradient(135deg,#F5A623,#f7b74a);border:3px solid #0B192C;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 8px rgba(0,0,0,0.3);"><span style="transform:rotate(45deg);color:#0B192C;font-weight:900;font-size:14px;">R</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
});

const urgentPin = new L.DivIcon({
    className: "renforteo-map-pin-urgent",
    html: `<div style="width:32px;height:32px;background:#dc2626;border:3px solid #7f1d1d;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 8px rgba(0,0,0,0.3);"><span style="transform:rotate(45deg);color:white;font-weight:900;font-size:14px;">!</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
});

export default function MissionsMap({ missions, center, radiusKm = 25, onApply }) {
    const geocoded = useMemo(
        () => missions.filter((m) => typeof m.lat === "number" && typeof m.lng === "number"),
        [missions]
    );
    // Default centre = candidate lat/lng, else Paris
    const cx = center?.lat ?? 48.8566;
    const cy = center?.lng ?? 2.3522;
    return (
        <div
            className="sd-card overflow-hidden"
            style={{ height: 500 }}
            data-testid="missions-map"
        >
            <MapContainer
                center={[cx, cy]}
                zoom={11}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {center && (
                    <>
                        <Circle
                            center={[cx, cy]}
                            radius={radiusKm * 1000}
                            pathOptions={{
                                color: "#F5A623",
                                fillColor: "#F5A623",
                                fillOpacity: 0.08,
                                weight: 2,
                                dashArray: "6 6",
                            }}
                        />
                        <Marker
                            position={[cx, cy]}
                            icon={new L.DivIcon({
                                className: "renforteo-map-me",
                                html: `<div style="width:20px;height:20px;background:#0B192C;border:3px solid white;border-radius:50%;box-shadow:0 0 0 3px #0B192C33;"></div>`,
                                iconSize: [20, 20],
                                iconAnchor: [10, 10],
                            })}
                        >
                            <Popup>Ma position</Popup>
                        </Marker>
                    </>
                )}
                {geocoded.map((m) => {
                    const urgent = m.urgency === "urgent" || m.urgency === "very_urgent";
                    return (
                        <Marker
                            key={m.id}
                            position={[m.lat, m.lng]}
                            icon={urgent ? urgentPin : goldPin}
                        >
                            <Popup>
                                <div style={{ minWidth: 200 }}>
                                    <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>
                                        {categoryLabel(m.job_category)}
                                    </div>
                                    <div style={{ fontWeight: 700, marginBottom: 4, color: "#0B192C" }}>{m.title}</div>
                                    <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
                                        {m.city} ({m.postal_code}) · {m.date} · {m.start_time}–{m.end_time}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#0B192C", marginBottom: 6 }}>
                                        <b>{fmtEUR(m.hourly_gross)}/h</b> · {m.people_count} pers.
                                        {typeof m.distance_km === "number" && (
                                            <span> · {m.distance_km.toFixed(1)} km</span>
                                        )}
                                    </div>
                                    {urgent && (
                                        <div style={{ fontSize: 11, color: "#dc2626", fontWeight: 600, marginBottom: 6 }}>
                                            ⚡ Mission {m.urgency === "very_urgent" ? "très urgente" : "urgente"}
                                        </div>
                                    )}
                                    {onApply && (
                                        <button
                                            onClick={() => onApply(m)}
                                            style={{
                                                background: "#F5A623",
                                                color: "#0B192C",
                                                border: "none",
                                                padding: "6px 12px",
                                                borderRadius: 8,
                                                fontWeight: 700,
                                                fontSize: 12,
                                                cursor: "pointer",
                                                width: "100%",
                                            }}
                                        >
                                            Voir la mission
                                        </button>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
            {geocoded.length < missions.length && (
                <div className="p-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100">
                    {missions.length - geocoded.length} mission(s) sans géolocalisation ne
                    sont pas affichée(s) sur la carte.
                </div>
            )}
        </div>
    );
}
